import { useState, useEffect, useMemo } from "react";
import { Loader2, Calendar, Clock, MapPin, Truck, Check, X, MessageSquare, Filter, Plus, ChevronRight, Search, Archive, Inbox } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { EmailStatusBadge } from "../components/admin/EmailStatus";
import { AdminAppointmentDetailModal } from "../components/admin/AdminAppointmentDetailModal";
import { AdminAppointmentCreateModal } from "../components/admin/AdminAppointmentCreateModal";
import { toast } from "sonner";
import { AdminLoadingState } from "../components/admin/AdminErrorBoundary";
import axios from "axios";
import { melbourneTodayISO, bucketAppointments } from "../utils/appointmentClassify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return localStorage.getItem("adminToken") || "";
}
const hdrs = () => ({ Authorization: `Bearer ${getToken()}` });

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
}

// ─── Section 9 — Upcoming vs History classification ─────────────────────────
// Classification helpers live in ../utils/appointmentClassify.js so the rules
// are testable and reusable across admin views.

// Human labels for the status filter + pill display.
const STATUS_LABELS = {
  confirmed: "Confirmed", pending: "Pending", completed: "Completed",
  converted: "Converted", follow_up: "Follow up", closed: "Closed",
  no_show: "No-show", cancelled: "Cancelled",
  customer_cancelled: "Customer cancelled", admin_cancelled: "Admin cancelled",
  rescheduled: "Rescheduled",
};
function statusPillClasses(status) {
  const s = (status || "").toLowerCase();
  if (s === "confirmed") return "bg-green-50 text-green-600";
  if (s === "completed") return "bg-blue-50 text-blue-600";
  if (s === "converted") return "bg-emerald-50 text-emerald-700";
  if (s === "follow_up") return "bg-amber-100 text-amber-700";
  if (s === "closed") return "bg-slate-100 text-slate-600";
  if (s === "no_show") return "bg-gray-100 text-gray-500";
  if (s === "rescheduled") return "bg-indigo-50 text-indigo-600";
  if (["cancelled", "customer_cancelled", "admin_cancelled"].includes(s)) return "bg-red-50 text-red-500";
  return "bg-amber-50 text-amber-600";
}

// ─── Section 18 — Trial attendance pill ─────────────────────────────────
function attendancePillClasses(a) {
  const s = (a || "awaiting_response").toLowerCase();
  if (s === "confirmed_attending") return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  if (s === "not_attending") return "bg-red-50 text-red-600 border border-red-200";
  if (s === "no_response") return "bg-slate-100 text-slate-600 border border-slate-200";
  if (s === "cancelled") return "bg-gray-100 text-gray-500 border border-gray-200";
  if (s === "completed") return "bg-blue-50 text-blue-600 border border-blue-200";
  return "bg-amber-50 text-amber-700 border border-amber-200"; // awaiting_response
}
function attendanceLabel(a) {
  const s = (a || "awaiting_response").toLowerCase();
  return ({
    awaiting_response: "Awaiting response",
    confirmed_attending: "Attending",
    not_attending: "Not attending",
    no_response: "No response",
    cancelled: "Cancelled",
    completed: "Completed",
  })[s] || s.replace("_", " ");
}

function AppointmentRow({ appt, onClick }) {
  const isPickup = appt.appointment_type === "pickup";
  const isTrial = appt.appointment_type === "trial";
  const adminEmailType = `appointment_admin_${appt.appointment_type}_new`;
  const status = (appt.status || "confirmed").toLowerCase();
  return (
    <div className="py-3 border-b border-[#282828]/5 last:border-0 cursor-pointer hover:bg-[#FAF8F0]/40 -mx-2 px-2" data-testid={`appt-row-${appt.id}`} onClick={() => onClick?.(appt)}>
      <div className="flex items-center gap-4">
        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${isPickup ? 'bg-[#D4B8B4]/20' : isTrial ? 'bg-[#a855f7]/15' : 'bg-[#B8C9BD]/20'}`}>
          {isPickup ? <MapPin className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} /> : isTrial ? <Calendar className="w-4 h-4 text-[#a855f7]" strokeWidth={1.5} /> : <Truck className="w-4 h-4 text-[#6B8F71]" strokeWidth={1.5} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] text-[#282828] font-medium">{appt.customer_name || "Customer"}</p>
          <p className="text-[11px] text-[#676664]/60">{appt.customer_email} &middot; {appt.customer_phone}</p>
          {appt.outcome && <p className="text-[10px] text-[#9A7B4F] mt-0.5">Outcome: {STATUS_LABELS[appt.outcome] || appt.outcome}{appt.outcome_notes ? ` — ${appt.outcome_notes.slice(0, 60)}${appt.outcome_notes.length > 60 ? "…" : ""}` : ""}</p>}
        </div>
        <div className="text-right">
          <p className="text-[12px] text-[#282828]">{appt.date ? new Date(appt.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : ""}</p>
          <p className="text-[12px] text-[#9A7B4F] font-medium">{formatTime(appt.time)}</p>
        </div>
        <span className="text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium bg-[#F5F0E6] text-[#676664]" data-testid={`appt-type-${appt.id}`}>
          {(appt.appointment_type || "appt").replace("_", " ")}
        </span>
        <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium ${statusPillClasses(status)}`} data-testid={`appt-status-${appt.id}`}>
          {(STATUS_LABELS[status] || status).replace("_", " ")}
        </span>
        {isTrial && (
          <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium ${attendancePillClasses(appt.attendance_status)}`}
                data-testid={`attendance-status-${appt.id}`}>
            {attendanceLabel(appt.attendance_status)}
          </span>
        )}
        <ChevronRight className="w-3.5 h-3.5 text-[#676664]/30" />
      </div>
      <div className="mt-2 ml-12 space-y-1 bg-[#FAF8F0]/60 border border-[#282828]/8 px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-0.5">Emails</div>
        <EmailStatusBadge entityId={appt.id} emailType={adminEmailType} label="Confirmation → admin" compact />
        {isTrial && <EmailStatusBadge entityId={appt.id} emailType="trial_booking_customer_confirmed" label="Confirmation → customer" compact />}
      </div>
    </div>
  );
}

function RequestRow({ req, onAction }) {
  const [processing, setProcessing] = useState(false);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  const handleAction = async (action) => {
    setProcessing(true);
    await onAction(req.id, action, notes);
    setProcessing(false);
    setShowNotes(false);
  };

  return (
    <div className="border border-[#282828]/8 p-4 bg-white" data-testid={`request-row-${req.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] text-[#282828] font-medium">{req.customer_name}</p>
          <p className="text-[11px] text-[#676664]/60">{req.customer_email} &middot; {req.customer_phone}</p>
          <div className="flex items-center gap-3 mt-2 text-[12px] text-[#5A5A5A]">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {req.requested_date}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {req.requested_time}</span>
            <span className={`text-[10px] px-1.5 py-0.5 ${req.appointment_type === "pickup" ? 'bg-[#D4B8B4]/20 text-[#9A7B4F]' : 'bg-[#B8C9BD]/20 text-[#6B8F71]'}`}>
              {req.appointment_type === "pickup" ? "Pick-up" : "Drop-off"}
            </span>
          </div>
          {req.message && (
            <div className="mt-2 flex items-start gap-1.5">
              <MessageSquare className="w-3 h-3 text-[#676664]/40 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-[#676664]/60 italic">{req.message}</p>
            </div>
          )}
        </div>
        <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium flex-shrink-0 ${req.status === "pending" ? 'bg-amber-50 text-amber-600' : req.status === "approved" ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
          {req.status}
        </span>
      </div>

      {req.status === "pending" && (
        <div className="mt-3 pt-3 border-t border-[#282828]/6">
          {showNotes && (
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Admin notes (optional)..." rows={2}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs mb-2 focus:border-[#9A7B4F] focus:outline-none resize-none" data-testid={`admin-notes-${req.id}`} />
          )}
          <div className="flex items-center gap-2">
            {!showNotes && (
              <button onClick={() => setShowNotes(true)} className="text-[10px] text-[#676664]/40 hover:text-[#676664]">Add notes</button>
            )}
            <div className="ml-auto flex gap-2">
              <button onClick={() => handleAction("reject")} disabled={processing}
                className="px-4 py-1.5 text-[10px] text-red-500 border border-red-200 hover:bg-red-50 tracking-wider uppercase" data-testid={`reject-${req.id}`}>
                {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><X className="w-3 h-3 inline mr-1" />Reject</>}
              </button>
              <button onClick={() => handleAction("approve")} disabled={processing}
                className="px-4 py-1.5 text-[10px] text-white bg-[#9A7B4F] hover:bg-[#8B6B3F] tracking-wider uppercase" data-testid={`approve-${req.id}`}>
                {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 inline mr-1" />Approve</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // Section 9 — default tab is "upcoming". History is a peer tab, requests kept.
  const [tab, setTab] = useState("upcoming");
  const [typeFilter, setTypeFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Section 9 — filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");   // exact status
  const [outcomeFilter, setOutcomeFilter] = useState(""); // exact outcome
  const [quickPill, setQuickPill] = useState("");         // "converted" | "no_show" | "cancelled"
  const [attendanceFilter, setAttendanceFilter] = useState(""); // Section 18 — trial attendance
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async () => {
    try {
      const [apptRes, reqRes] = await Promise.all([
        axios.get(`${API}/admin/appointments`, { headers: hdrs() }),
        axios.get(`${API}/admin/appointment-requests?status=`, { headers: hdrs() }),
      ]);
      setAppointments(apptRes.data.appointments || []);
      setRequests(reqRes.data.requests || []);
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleRequestAction = async (reqId, action, adminNotes) => {
    try {
      await axios.patch(`${API}/admin/appointment-requests/${reqId}`, { action, admin_notes: adminNotes }, { headers: hdrs() });
      toast.success(action === "approve" ? "Request approved — appointment created" : "Request rejected");
      fetchData();
    } catch { toast.error("Action failed"); }
  };

  const pendingCount = (requests || []).filter(r => r.status === "pending").length;

  // ── Section 9 classification + filter application ─────────────────────────
  const { upcomingAppts, historyAppts, hiddenCount } = useMemo(() => {
    const todayISO = melbourneTodayISO();
    const buckets = bucketAppointments(appointments || [], todayISO);
    return {
      upcomingAppts: buckets.upcoming,
      historyAppts: buckets.history,
      hiddenCount: buckets.hidden.length,
    };
  }, [appointments]);

  const applyFilters = (list) => {
    const q = customerQuery.trim().toLowerCase();
    return list.filter(a => {
      if (typeFilter && a.appointment_type !== typeFilter) return false;
      if (statusFilter && (a.status || "").toLowerCase() !== statusFilter) return false;
      if (outcomeFilter && (a.outcome || "").toLowerCase() !== outcomeFilter) return false;
      if (quickPill) {
        // Quick pills combine status + outcome semantics into one click.
        if (quickPill === "converted" && !((a.status === "converted") || (a.outcome === "converted"))) return false;
        if (quickPill === "no_show"   && !((a.status === "no_show")   || (a.outcome === "no_show")))   return false;
        if (quickPill === "cancelled" && !(["cancelled", "customer_cancelled", "admin_cancelled"].includes((a.status || "").toLowerCase()))) return false;
      }
      if (attendanceFilter && (a.attendance_status || "awaiting_response") !== attendanceFilter) return false;
      if (dateFrom && (a.date || "") < dateFrom) return false;
      if (dateTo && (a.date || "") > dateTo) return false;
      if (q) {
        const hay = [a.customer_name, a.customer_email, a.customer_phone, a.id].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  };

  const shownAppts = applyFilters(tab === "history" ? historyAppts : upcomingAppts);

  const anyFilterActive = !!(dateFrom || dateTo || customerQuery.trim() || statusFilter || outcomeFilter || quickPill || typeFilter || attendanceFilter);
  const clearFilters = () => {
    setDateFrom(""); setDateTo(""); setCustomerQuery("");
    setStatusFilter(""); setOutcomeFilter(""); setQuickPill(""); setTypeFilter("");
    setAttendanceFilter("");
  };

  if (loading) return <AdminLoadingState message="Loading appointments..." />;

  return (
    <AdminLayout
      title="Appointments"
      subtitle="Manage customer pick-up and drop-off appointments"
      actions={
        <Button onClick={() => setCreateOpen(true)} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="appt-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Appointment
        </Button>
      }
    >
      {/* Section 9 — Upcoming | History | Requests */}
      <div className="flex gap-1 mb-6 border-b border-[#282828]/8">
        {[
          { key: "upcoming", label: `Upcoming${upcomingAppts.length ? ` (${upcomingAppts.length})` : ""}`, icon: Inbox },
          { key: "history",  label: `History${historyAppts.length ? ` (${historyAppts.length})` : ""}`,   icon: Archive },
          { key: "requests", label: `Requests${pendingCount > 0 ? ` (${pendingCount})` : ""}`, icon: MessageSquare },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-[12px] tracking-wider transition-colors border-b-2 -mb-[1px] inline-flex items-center gap-1.5 ${tab === t.key ? 'border-[#9A7B4F] text-[#9A7B4F] font-medium' : 'border-transparent text-[#676664]/50 hover:text-[#676664]'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t.label}
          </button>
        ))}
      </div>

      {(tab === "upcoming" || tab === "history") && (
        <div>
          {/* Row 1: type + quick outcome pills + filters toggle */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-[#676664]/40" />
            <div className="flex gap-1">
              {[{ key: "", label: "All types" }, { key: "trial", label: "Trials" }, { key: "pickup", label: "Pick-ups" }, { key: "dropoff", label: "Drop-offs" }, { key: "delivery", label: "Deliveries" }].map(f => (
                <button key={f.key} onClick={() => setTypeFilter(f.key)}
                  className={`px-3 py-1 text-[10px] tracking-wider border ${typeFilter === f.key ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' : 'bg-white text-[#676664] border-[#282828]/12'}`}
                  data-testid={`filter-type-${f.key || 'all'}`}>{f.label}</button>
              ))}
            </div>
            {tab === "history" && (
              <div className="flex gap-1 ml-2">
                {[{ key: "converted", label: "Converted" }, { key: "no_show", label: "No-show" }, { key: "cancelled", label: "Cancelled" }].map(p => (
                  <button key={p.key} onClick={() => setQuickPill(quickPill === p.key ? "" : p.key)}
                    className={`px-3 py-1 text-[10px] tracking-wider border ${quickPill === p.key ? 'bg-[#282828] text-white border-[#282828]' : 'bg-white text-[#676664] border-[#282828]/12'}`}
                    data-testid={`filter-quick-${p.key}`}>{p.label}</button>
                ))}
              </div>
            )}
            <button onClick={() => setShowFilters(v => !v)}
              className={`ml-auto px-3 py-1 text-[10px] tracking-wider border inline-flex items-center gap-1 ${showFilters || anyFilterActive ? 'bg-[#9A7B4F]/10 text-[#9A7B4F] border-[#9A7B4F]/30' : 'bg-white text-[#676664] border-[#282828]/12'}`}
              data-testid="toggle-filters">
              <Filter className="w-3 h-3" /> {showFilters ? "Hide filters" : "Filters"}{anyFilterActive ? " ●" : ""}
            </button>
          </div>

          {/* Row 2: expanded filters */}
          {showFilters && (
            <div className="bg-white border border-[#282828]/8 p-3 mb-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="filters-panel">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1 block">Customer</span>
                <div className="relative">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[#676664]/40" />
                  <input type="text" value={customerQuery} onChange={e => setCustomerQuery(e.target.value)}
                    placeholder="Name, email, phone…"
                    className="w-full border border-[#282828]/15 pl-6 pr-2 py-1 text-[11px] focus:outline-none focus:border-[#9A7B4F]"
                    data-testid="filter-customer-input" />
                </div>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1 block">Date from</span>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="w-full border border-[#282828]/15 px-2 py-1 text-[11px] focus:outline-none focus:border-[#9A7B4F]"
                  data-testid="filter-date-from" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1 block">Date to</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="w-full border border-[#282828]/15 px-2 py-1 text-[11px] focus:outline-none focus:border-[#9A7B4F]"
                  data-testid="filter-date-to" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1 block">Status</span>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                  className="w-full border border-[#282828]/15 px-2 py-1 text-[11px] focus:outline-none focus:border-[#9A7B4F] bg-white"
                  data-testid="filter-status-select">
                  <option value="">Any</option>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1 block">Outcome</span>
                <select value={outcomeFilter} onChange={e => setOutcomeFilter(e.target.value)}
                  className="w-full border border-[#282828]/15 px-2 py-1 text-[11px] focus:outline-none focus:border-[#9A7B4F] bg-white"
                  data-testid="filter-outcome-select">
                  <option value="">Any</option>
                  {["converted","follow_up","did_not_convert","no_show","customer_cancelled","admin_cancelled","rescheduled","could_not_contact","duplicate","other"].map(o => (
                    <option key={o} value={o}>{STATUS_LABELS[o] || o.replace("_", " ")}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1 block">Trial attendance</span>
                <select value={attendanceFilter} onChange={e => setAttendanceFilter(e.target.value)}
                  className="w-full border border-[#282828]/15 px-2 py-1 text-[11px] focus:outline-none focus:border-[#9A7B4F] bg-white"
                  data-testid="filter-attendance-select">
                  <option value="">Any</option>
                  <option value="awaiting_response">Awaiting response</option>
                  <option value="confirmed_attending">Confirmed attending</option>
                  <option value="not_attending">Not attending</option>
                  <option value="no_response">No response</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </label>
              {anyFilterActive && (
                <div className="md:col-span-2 lg:col-span-4 flex justify-end">
                  <button onClick={clearFilters}
                    className="px-3 py-1 text-[10px] tracking-wider text-[#9A7B4F] hover:underline"
                    data-testid="clear-filters">Clear all filters</button>
                </div>
              )}
            </div>
          )}

          {/* Rescheduled note */}
          {tab === "history" && hiddenCount > 0 && (
            <div className="text-[10px] text-[#676664]/60 mb-2 italic" data-testid="hidden-count-note">
              {hiddenCount} rescheduled appointment{hiddenCount === 1 ? "" : "s"} hidden because {hiddenCount === 1 ? "its replacement is" : "their replacements are"} still active.
            </div>
          )}

          {/* Results */}
          {shownAppts.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[#282828]/8" data-testid="empty-state">
              {tab === "history" ? <Archive className="w-8 h-8 text-[#676664]/15 mx-auto mb-3" strokeWidth={1} /> : <Calendar className="w-8 h-8 text-[#676664]/15 mx-auto mb-3" strokeWidth={1} />}
              <p className="text-[13px] text-[#676664]/40">
                {tab === "history"
                  ? (anyFilterActive ? "No history matches these filters" : "No history yet — closed appointments will land here.")
                  : (anyFilterActive ? "No upcoming appointments match these filters" : "No upcoming appointments.")}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-[#282828]/8 p-4" data-testid={`appt-list-${tab}`}>
              {shownAppts.map(appt => <AppointmentRow key={appt.id} appt={appt} onClick={() => setDetailId(appt.id)} />)}
            </div>
          )}
        </div>
      )}

      {tab === "requests" && (
        <div className="space-y-3">
          {requests.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[#282828]/8">
              <MessageSquare className="w-8 h-8 text-[#676664]/15 mx-auto mb-3" strokeWidth={1} />
              <p className="text-[13px] text-[#676664]/40">No requests yet</p>
            </div>
          ) : (
            requests.map(req => <RequestRow key={req.id} req={req} onAction={handleRequestAction} />)
          )}
        </div>
      )}

      {detailId && (
        <AdminAppointmentDetailModal
          appointmentId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={fetchData}
        />
      )}
      {createOpen && (
        <AdminAppointmentCreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { fetchData(); }}
        />
      )}
    </AdminLayout>
  );
}
